import {
  ContactCandidates,
  ContactEnrichments,
  EliminatedPerson,
  EnrichedContact,
  EnrichmentConfidence,
  HUBSPOT_ONLY_PREFIX,
  RELATIONSHIP_RANK,
  candidateDisplayName,
  contactDisplayName,
  promotionEligibility,
  HubSpotMatches,
  HubSpotRoleResult,
  PeopleCandidate,
  PeopleRole,
  RELATIONSHIP_CHIP,
  Relationship,
  relationshipDetail,
  relationshipFor,
  useApolloQuota,
  useHubSpotMatchTrace,
  useHubSpotPushContact,
  useLead,
  useLeadContactThread,
  useLeadActivePeopleOps,
  useLeadOutreachThreads,
  useOperation,
  usePeopleDiscovery,
  usePeopleDiscoveryTrace,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import CollapsibleCell from "./CollapsibleCell";
import OutreachDraftModal from "./OutreachDraftModal";
import {
  mergeContactEnrichments,
  mergeHubSpotRoleResults,
  mergePeopleRoleResults,
} from "./poolContacts";

// Mirror of the BE `_company_name_from_decision`: pull the chosen company name
// out of a role decision, tolerating the nested auto-pick shape and the flat one.
const companyFromDecision = (
  decision: Record<string, unknown> | null
): string => {
  if (!decision) return "";
  const payload =
    typeof decision.payload === "object" && decision.payload !== null
      ? (decision.payload as Record<string, unknown>)
      : {};
  for (const source of [payload, decision]) {
    for (const key of ["displayName", "orgName", "companyName"]) {
      const raw = source[key];
      if (typeof raw === "string" && raw.trim()) return raw.trim();
    }
  }
  return "";
};

// The AI ranker's priority bands, best-first, with the operator-facing label.
const PRIORITY_ORDER: { band: string; label: string }[] = [
  { band: "primary", label: "Primary target" },
  { band: "secondary", label: "Also strong" },
  { band: "sustainability", label: "Sustainability / ESG" },
  { band: "other", label: "Other" },
];
const bandRank = (band: string): number => {
  const i = PRIORITY_ORDER.findIndex((p) => p.band === band);
  return i === -1 ? PRIORITY_ORDER.length : i;
};
const bandLabel = (band: string): string =>
  PRIORITY_ORDER.find((p) => p.band === band)?.label ?? "Other";

// How many name/position rows the collapsed cell previews before "+N more".
const PREVIEW_LIMIT = 8;

// Past this many rows a candidates table scrolls inside itself rather than growing the
// page: a 40-person company otherwise pushes the sibling role cell and the promote
// button off screen, and the operator loses the thing they came here to click.
const TABLE_SCROLL_ROWS = 12;
const TABLE_SCROLL_MAX_HEIGHT = 420;

// Wraps a candidates table so it only scrolls once it is actually long. Shared by the
// ranked and the excluded table so the two behave the same way.
const ScrollableRows: React.FC<{
  rowCount: number;
  children: React.ReactNode;
}> = ({ rowCount, children }) =>
  rowCount > TABLE_SCROLL_ROWS ? (
    <div
      style={{
        maxHeight: TABLE_SCROLL_MAX_HEIGHT,
        overflowY: "auto",
        border: "1px solid #e9ecef",
        borderRadius: 4,
      }}
    >
      {children}
    </div>
  ) : (
    <>{children}</>
  );

// A role with a chosen company but no contacts yet may have a discovery running
// server-side that this cell didn't kick off - the promote handler auto-runs free
// discovery, and a landlord/tenant company change re-runs it, but the inbox has no op
// to poll. So briefly poll the lead when a role is in that state (the first fetch
// catches an already-landed run; the polls catch one still in flight), bounded so a
// role that genuinely has no people stops after ~a minute. Each tick refetches the
// ACTIVE-OPS query as well as the lead, so an op created moments after this cell
// mounted is still picked up (that lookup is what drives the "working" spinner).
const AUTO_DISCOVERY_CHECK_LIMIT = 10;
const AUTO_DISCOVERY_CHECK_INTERVAL_MS = 6000;

type ChipStyle = { label: string; bg: string; fg: string };

// Email-confidence chip: the BE confidence bucket (persisted on each record by
// confidence_from_status) as a coloured label. Never a fabricated percentage - just the
// vendor signal. "undeliverable" is the BE's known-bad bucket (bounced/unavailable/
// invalid); it must NOT read as a mailable grey "Unverified", so it gets a red danger
// chip. The FE renders the persisted bucket directly - the BE owns the mapping, so the
// two surfaces agree by construction (no client-side re-derivation from the raw status).
const CONFIDENCE_CHIP: Record<EnrichmentConfidence, ChipStyle> = {
  verified: { label: "Verified", bg: "#d1e7dd", fg: "#0f5132" },
  likely: { label: "Likely", bg: "#fff3cd", fg: "#664d03" },
  unverified: { label: "Unverified", bg: "#e9ecef", fg: "#495057" },
  undeliverable: { label: "Undeliverable", bg: "#f8d7da", fg: "#842029" },
  none: { label: "No email", bg: "#e9ecef", fg: "#6c757d" },
};

const ConfidenceChip: React.FC<{ contact: EnrichedContact }> = ({
  contact,
}) => {
  const chip = CONFIDENCE_CHIP[contact.confidence] ?? CONFIDENCE_CHIP.none;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        padding: "2px 9px",
        whiteSpace: "nowrap",
        background: chip.bg,
        color: chip.fg,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "currentColor",
          opacity: 0.85,
        }}
      />
      {chip.label}
    </span>
  );
};

// The HubSpot line above the candidates table: what we matched the company to, how many
// contacts we hold, and when it was read. The read timestamp is shown rather than
// implied - the cache is minutes old by design, and an operator who has just logged
// something in HubSpot needs to know whether this reflects it.
const HubSpotSummary: React.FC<{
  hubspot: HubSpotRoleResult | null;
  busy: boolean;
  onRefresh: () => void;
  onViewTrace: () => void;
}> = ({ hubspot, busy, onRefresh, onViewTrace }) => {
  if (!hubspot) return null;
  const failed =
    hubspot.status === "unavailable" || hubspot.status === "not_configured";
  const known = (hubspot.contacts ?? []).length;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        marginTop: 7,
        padding: "7px 9px",
        background: failed ? "#fff8f8" : "#f1f8f4",
        border: `1px solid ${failed ? "#f5c2c7" : "#d1e7dd"}`,
        borderRadius: 6,
        fontSize: 12,
      }}
    >
      {failed ? (
        <span style={{ color: "#842029", fontWeight: 600 }}>
          {hubspot.status === "not_configured"
            ? "HubSpot is not configured on this environment"
            : "Could not read HubSpot"}
          {/* The BE already resolved WHY and persists it; without it the banner is a
              dead end that reads like an environment someone forgot to set up, and
              the actual cause (a revoked token, a missing scope) is only in the
              worker logs - which cost a QA cycle to go and read. */}
          {hubspot.error && (
            <span
              style={{
                display: "block",
                fontWeight: 400,
                color: "#842029",
                marginTop: 2,
              }}
            >
              {hubspot.error}
            </span>
          )}
        </span>
      ) : hubspot.company ? (
        <span style={{ color: "#0f5132", fontWeight: 600 }}>
          In HubSpot: {hubspot.company.name ?? hubspot.companyName}
        </span>
      ) : (
        <span style={{ color: "#6c757d", fontWeight: 600 }}>
          Not in HubSpot
        </span>
      )}
      <span className="text-muted">
        {hubspot.company?.domain
          ? `matched on domain ${hubspot.company.domain} · `
          : ""}
        {known} known contact{known === 1 ? "" : "s"}
      </span>
      <span style={{ flexGrow: 1 }} />
      <span style={{ color: "#9aa2ad", fontSize: 11.5 }}>
        read {hubspot.readAt ? new Date(hubspot.readAt).toLocaleString() : "-"}
      </span>
      {hubspot.traceRunId && (
        <Button
          variant="link"
          className="p-0"
          style={{ fontSize: 12, verticalAlign: "baseline" }}
          onClick={(e) => {
            e.stopPropagation();
            onViewTrace();
          }}
        >
          View matching trace
        </Button>
      )}
      <Button
        variant="link"
        className="p-0"
        style={{ fontSize: 12, verticalAlign: "baseline" }}
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          onRefresh();
        }}
      >
        {busy ? "Refreshing..." : "Refresh"}
      </Button>
    </div>
  );
};

// The relationship adorner. Rendered in BOTH the candidates table and the contact list
// from one component reading one shared map, so the two surfaces cannot drift apart on
// what they call a relationship or what colour it is.
const RelationshipChip: React.FC<{ relationship: Relationship }> = ({
  relationship,
}) => {
  const chip = RELATIONSHIP_CHIP[relationship.kind];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        padding: "2px 9px",
        whiteSpace: "nowrap",
        background: chip.bg,
        color: chip.fg,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "currentColor",
          opacity: 0.85,
        }}
      />
      {chip.label}
    </span>
  );
};

// The adorner plus its sub-line: the chip says what our relationship is, the line under
// it says where the row came from and the one fact that matters for that kind. Both
// tables use this, so "In HubSpot / Apollo + HubSpot" and "In HubSpot / HubSpot only"
// read differently wherever they appear.
const RelationshipCell: React.FC<{
  relationship: Relationship;
  onConfirm?: () => void;
  onReject?: () => void;
  busy?: boolean;
}> = ({ relationship, onConfirm, onReject, busy }) => (
  <>
    <RelationshipChip relationship={relationship} />
    <div style={{ color: "#6c757d", fontSize: 11, marginTop: 2 }}>
      {relationshipDetail(relationship)}
    </div>
    {relationship.kind === "possible_match" && onConfirm && onReject && (
      <div style={{ display: "flex", gap: 6, marginTop: 3 }}>
        <Button
          size="sm"
          variant="outline-primary"
          style={{ fontSize: 11, padding: "0 6px" }}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onConfirm();
          }}
        >
          Confirm
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          style={{ fontSize: 11, padding: "0 6px" }}
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onReject();
          }}
        >
          Not them
        </Button>
      </div>
    )}
  </>
);

// A person who is in HubSpot, was NOT in the Apollo results, and the ranking pass never
// saw. The worker now reads the CRM before ranking, so these people normally arrive as
// ranked candidates like everyone else; what lands here is the remainder - a contact
// added by a later Refresh, or a role whose blob predates that change. They are shown in
// the same table (they are exactly the people we already know) with no band, because
// there genuinely was no ranking to show.
const hubspotOnlyCandidates = (
  hubspot: HubSpotRoleResult | null | undefined,
  matchedContactIds: Set<string>,
  rankedIds: Set<string>
): PeopleCandidate[] =>
  (hubspot?.contacts ?? [])
    .filter(
      (contact) =>
        !matchedContactIds.has(contact.contactId) &&
        !rankedIds.has(`${HUBSPOT_ONLY_PREFIX}${contact.contactId}`)
    )
    .map((contact) => ({
      // Prefixed so it can never collide with a real Apollo id, and so the promote
      // handler can tell the two populations apart.
      apolloId: `${HUBSPOT_ONLY_PREFIX}${contact.contactId}`,
      firstName: contactDisplayName(contact),
      lastNameMasked: "",
      title: contact.title ?? "",
      orgName: contact.companyName ?? "",
      hasEmail: !!contact.email,
      hasDirectPhone: false,
      band: "other",
      roleLabel: "other",
      reasoning: "",
      modelOrder: 9999,
      promptRevision: 0,
      selected: false,
    }));

// One Contacts cell (landlord or tenant) in the leads right-panel grid, styled
// like the sibling Ownership cells. Collapsed: the company + an x/y contact
// count + a name/position preview, with the Find button in the footer. Expanded
// (full-width): the searches that ran, the ranked table + the excluded list (each
// with a select column), a "Pull contact info" action for the ticked people, and
// the AI trace. The pulled contacts land in the SEPARATE ContactDetailsCell below.
export const ContactsRoleCell: React.FC<{
  leadId: string;
  role: PeopleRole;
  decision: Record<string, unknown> | null;
  contactCandidates: ContactCandidates | null;
  contactEnrichments: ContactEnrichments | null;
  hubspotMatches: HubSpotMatches | null;
  expanded: boolean;
  onToggle: () => void;
  // Full-width row span - used when it's the sole Contacts cell (owner-occupier,
  // where only the occupier/tenant contacts are shown).
  span?: boolean;
  // Pool this cell's discovery with another role's (owner-occupier: the one company is
  // ranked under both personas, so the tenant cell pools in the landlord persona's people
  // deduped by apolloId). null = no pooling (the normal per-role cell). Required so a
  // caller can't silently forget it (see the "required typed props" rule).
  poolWithRole: PeopleRole | null;
}> = ({
  leadId,
  role,
  decision,
  contactCandidates,
  contactEnrichments,
  hubspotMatches,
  expanded,
  onToggle,
  span,
  poolWithRole,
}) => {
  const {
    findLeadPeople,
    pullContactInfo,
    refreshHubSpot,
    decideHubSpotMatch,
  } = usePeopleDiscovery();
  const queryClient = useQueryClient();
  const [op, setOp] = useState<{ key: string; siteId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which AI trace the modal is showing (the ranking pass or the domain-resolution pass),
  // by run_id + title; null when closed. Two separate traces, one shared viewer.
  const [traceModal, setTraceModal] = useState<{
    runId: string;
    title: string;
  } | null>(null);
  const [showEliminated, setShowEliminated] = useState(false);
  // Optional operator-typed domain - handed to Apollo for an exact org match when
  // the auto name search misses (e.g. a small company Apollo indexes under a domain).
  const [domainInput, setDomainInput] = useState("");
  // Ephemeral selection of apolloIds to pull (both ranked + excluded rows). Only the
  // PULL RESULTS persist; the tick state itself is local and clears after a pull.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pullOp, setPullOp] = useState<{ key: string; siteId: string } | null>(
    null
  );
  const [pullMessage, setPullMessage] = useState<string | null>(null);
  const [pullError, setPullError] = useState<string | null>(null);
  const [hubSpotOp, setHubSpotOp] = useState<{
    key: string;
    siteId: string;
  } | null>(null);
  const [hubSpotError, setHubSpotError] = useState<string | null>(null);

  const companyName = companyFromDecision(decision);
  const hasCompany = companyName.length > 0;

  // Re-attach a running Find-people / Pull-contact-info to this panel. The run keeps going
  // server-side when the operator switches rows, but the op handle lives in this
  // component's state, so a remount would otherwise lose it and paint the run as
  // "stopped". This lookup reconnects the poll to the live op (mirroring how landlord
  // research survives navigation), and the functional setState never clobbers a run just
  // kicked off from this cell.
  //
  // Guard per OP KEY, not once per mount. The anti-loop requirement is only "never
  // re-attach an op the completion effect just cleared"; a once-per-mount latch also
  // blocked attaching an op created AFTER the first response, which is exactly the
  // auto-kickoff case the poll below refetches for - the spinner would then show a run
  // it never attached to, so the completion effect never fired and the result never
  // landed without a reload.
  const activeOps = useLeadActivePeopleOps(leadId, role, hasCompany);
  const handledOpKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    handledOpKeys.current = new Set();
  }, [leadId, role]);
  useEffect(() => {
    const data = activeOps.data;
    if (!data) return;
    const dk = data.discoveryOperationKey;
    const ek = data.enrichmentOperationKey;
    // Guard per OP KEY. The auto-kicked run is created moments after the decision lands,
    // so the first lookup routinely answers "no live op" - a latch set on that answer
    // left the panel permanently detached: the button never entered its running state
    // and the result never appeared without a manual refresh. Keying on the individual
    // key also keeps the anti-loop property (never re-attach an op the completion effect
    // just cleared) while still allowing a genuinely NEW op - a later discovery run, or
    // an enrichment op that starts after a discovery one - to attach.
    if (dk && !handledOpKeys.current.has(dk)) {
      handledOpKeys.current.add(dk);
      setOp((cur) => cur ?? { key: dk, siteId: data.siteId });
    }
    if (ek && !handledOpKeys.current.has(ek)) {
      handledOpKeys.current.add(ek);
      setPullOp((cur) => cur ?? { key: ek, siteId: data.siteId });
    }
  }, [activeOps.data]);

  const primaryListBlob = contactCandidates?.[role] ?? null;
  // Pooled (owner-occupier) or plain: merging with a null "other" is the identity, so a
  // non-pooling cell (poolWithRole=null) sees exactly `primaryListBlob`.
  const listRoleBlob = mergePeopleRoleResults(
    primaryListBlob,
    poolWithRole ? (contactCandidates?.[poolWithRole] ?? null) : null
  );
  // The inbox /leads list omits each role's heavy `eliminated` array (payload bloat -
  // only rendered in the collapsed "excluded" section). Fetch the single lead to fill it
  // in, but ONLY for a role that actually COMPLETED a ranking - the only status carrying
  // an `eliminated` list. A marker blob (not_run / quota_exhausted / no_people) has none,
  // so a detail GET for it is pure waste; without this gate a bulk promote's not_run tail
  // would fire one GET per un-run lead across the inbox (an N+1 the markers introduce).
  // The list-sourced blob paints the count + preview immediately; the detail fetch fills
  // in `eliminated`. The completion effect below invalidates ["lead", leadId] to refetch.
  const hasRoleDiscovery = listRoleBlob?.status === "complete";
  // Auto-discovery follow-up (see AUTO_DISCOVERY_* above): when a role has a company but
  // NO blob yet (discovery genuinely unresolved) and nothing was kicked off from this
  // cell, fetch + briefly poll the lead so an auto-kicked run appears without a reload. A
  // present blob - a result OR a not_run/throttled marker - means the auto-run already
  // settled, so there is nothing to wait for (and no detail GET to fire).
  const [autoChecks, setAutoChecks] = useState(0);
  const autoPollEnabled =
    hasCompany &&
    // Gate on the PRIMARY role's own blob, not the pooled one: an OO tenant that never
    // ran should still trigger its own auto-discovery poll even if the landlord ran.
    primaryListBlob === null &&
    op === null &&
    autoChecks < AUTO_DISCOVERY_CHECK_LIMIT;
  const leadDetail = useLead(
    hasRoleDiscovery || autoPollEnabled ? leadId : undefined
  );
  const roleResult = mergePeopleRoleResults(
    leadDetail.data?.lead.contactCandidates?.[role] ?? primaryListBlob,
    poolWithRole
      ? (leadDetail.data?.lead.contactCandidates?.[poolWithRole] ??
          contactCandidates?.[poolWithRole] ??
          null)
      : null
  );
  // The PRIMARY role's own (unpooled) discovery - what this cell's Find/Pull actually
  // target. Auto-poll and pull-selection key off THIS, not the pooled `roleResult`, so a
  // pooled cell still waits for / acts on the primary role's own data.
  const primaryOwnBlob =
    leadDetail.data?.lead.contactCandidates?.[role] ?? primaryListBlob;
  // Gate on the primary's OWN blob (not the pooled roleResult): an OO tenant whose own
  // discovery hasn't landed should still auto-poll even if the landlord persona already
  // populated the pooled view.
  const awaitingAutoDiscovery = autoPollEnabled && primaryOwnBlob === null;
  // Whether a discovery run covering this (lead, role) is ACTUALLY in flight server-side.
  // `/leads/{id}/active-people` reuses the kickoff's in-flight scan, which matches the
  // sequential BULK auto-run's `pairs` as well as a single run - so this covers the
  // promote-time auto-discovery the operator never clicked. This, plus "we haven't heard
  // back yet", is what "still working" means. The old spinner keyed on `primaryOwnBlob
  // === null` instead, i.e. on the ABSENCE of a result, which is indistinguishable from
  // "no run was ever started" - so it span forever on every lead whenever discovery
  // couldn't run, and the ~1-min bound reset on each remount because `autoChecks` is
  // component state (navigate away and back and it started over).
  const discoveryPending =
    hasCompany &&
    primaryOwnBlob === null &&
    op === null &&
    (activeOps.isLoading || activeOps.data?.discoveryOperationKey != null);
  // The apolloIds the primary role actually discovered (candidates OR eliminated). The
  // pull endpoint validates ids against contactCandidates[role] (this cell's `role`), so
  // a person present ONLY under the pooled-in persona can't be pulled from here - it would
  // be dropped server-side. Selection is therefore offered only for ids in this set; for a
  // non-pooled cell it contains every rendered id, so nothing changes.
  const primaryKnownIds = new Set<string>([
    ...(primaryOwnBlob?.candidates ?? []).map((c) => c.apolloId),
    ...(primaryOwnBlob?.eliminated ?? [])
      .map((p) => p.apolloId)
      .filter((x): x is string => !!x),
  ]);
  useEffect(() => {
    if (!awaitingAutoDiscovery) return;
    // Re-schedules each tick (autoChecks changes); invalidate via the stable
    // queryClient rather than the per-render leadDetail object so the timeout isn't
    // reset every render. The first fetch already fired when useLead enabled.
    const t = setTimeout(() => {
      setAutoChecks((n) => n + 1);
      void queryClient.invalidateQueries(["lead", leadId]);
      // Re-check for the auto-kicked op too: it may not have existed when this cell
      // mounted. Attaching to it is what puts the button into its running state and
      // keeps the poll alive past this bounded loop - and that lookup, not the absence
      // of a result, is what the waiting state keys on.
      void queryClient.invalidateQueries(["leadActivePeopleOps", leadId, role]);
    }, AUTO_DISCOVERY_CHECK_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [awaitingAutoDiscovery, autoChecks, queryClient, leadId, role]);

  const candidates: PeopleCandidate[] = [
    ...(roleResult?.candidates ?? []),
  ].sort(
    (a, b) => bandRank(a.band) - bandRank(b.band) || a.modelOrder - b.modelOrder
  );
  const eliminated: EliminatedPerson[] = roleResult?.eliminated ?? [];
  // People the AI ranking actually saw: the ones it kept plus the ones it excluded.
  // NOT the same as Apollo's `apolloTotalEntries`, which is how many people Apollo says
  // it has at the company - the client drops any Apollo row with no id or no job title
  // before ranking (a title-only persona cannot rank a person with no title), and a
  // large company is sampled (only the first pages are pulled). So `0/4 contacts` beside
  // "Show 3 excluded" left the 4th unaccounted for; the ratio now counts what was
  // considered, and the detail line below still carries Apollo's figure when the two
  // differ - "considered of N", which is true for both the dropped and the sampled case.
  const consideredCount = candidates.length + eliminated.length;
  const notConsideredCount = Math.max(
    0,
    (roleResult?.apolloTotalEntries ?? 0) - consideredCount
  );
  // The raw "Searches run" log only earns its place when it shows something the "Domain
  // used" headline + result count don't: a fallback chain (a rejected domain then a name
  // search) or a name search (whose exact query differs from the company name). A single
  // successful domain search is fully captured by "Domain used", so hide it as noise.
  const searchAttempts = roleResult?.attempts ?? [];
  const searchLogRedundant =
    searchAttempts.length === 1 &&
    searchAttempts[0].kind === "domain" &&
    searchAttempts[0].query === roleResult?.domain;
  // A throttle marker (Apollo daily/rate limit) persisted with no candidates - show
  // "throttled - retry" rather than an empty result that reads as "found nobody".
  const throttled =
    roleResult?.status === "quota_exhausted" && candidates.length === 0;
  // A not_run marker: the sequential bulk auto-run never reached this pair (past its
  // cap / soft budget, or the shared daily quota ran out). Persisted with no candidates
  // so the cell reads "not run yet - click Find people" rather than an empty result that
  // reads as "found nobody".
  const notRun = roleResult?.status === "not_run" && candidates.length === 0;

  // The apolloIds already pulled for this role (freshest of the single-lead fetch and
  // the list prop). Used to mark done rows and to compute how many credits a pull will
  // actually spend (already-pulled rows are skipped server-side, no re-charge).
  const enrichResult = mergeContactEnrichments(
    leadDetail.data?.lead.contactEnrichments?.[role] ??
      contactEnrichments?.[role] ??
      null,
    poolWithRole
      ? (leadDetail.data?.lead.contactEnrichments?.[poolWithRole] ??
          contactEnrichments?.[poolWithRole] ??
          null)
      : null
  );
  const enrichedIds = new Set(
    (enrichResult?.contacts ?? []).map((c) => c.apolloId)
  );

  // What HubSpot holds for this role (freshest of the single-lead fetch and the list
  // prop, mirroring how the candidates and enrichments above are resolved).
  // Pooled the same way the candidates are (OO leads rank one company under both
  // personas), so a pooled-in person keeps the adorner that belongs to them.
  const hubspotResult = mergeHubSpotRoleResults(
    leadDetail.data?.lead.hubspotMatches?.[role] ??
      hubspotMatches?.[role] ??
      null,
    poolWithRole
      ? (leadDetail.data?.lead.hubspotMatches?.[poolWithRole] ??
          hubspotMatches?.[poolWithRole] ??
          null)
      : null
  );
  const matchedContactIds = new Set(
    Object.values(hubspotResult?.matches ?? {}).map(
      (m) => (m as { contactId: string }).contactId
    )
  );
  // ONE list: everyone Apollo found AND everyone HubSpot holds at this company, deduped.
  // A HubSpot-only person is not a separate section - they are exactly the people we
  // already know, and Apollo failing to return them is not a reason to hide them.
  const rankedIds = new Set(candidates.map((c) => c.apolloId));
  const hubspotOnly = hubspotOnlyCandidates(
    hubspotResult,
    matchedContactIds,
    rankedIds
  );
  // Rows the ranking pass never saw, so the band/reasoning columns say so rather than
  // rendering a synthesised "other". Keyed on the row, not on the id shape: a CRM person
  // IS ranked now, and only the post-run remainder lands here.
  const unranked = new Set(hubspotOnly.map((c) => c.apolloId));
  const relationshipOf = (apolloId: string): Relationship =>
    relationshipFor(apolloId, hubspotResult, {
      hubspotOnly: apolloId.startsWith(HUBSPOT_ONLY_PREFIX),
    });
  // People we already know lead the list - they cost no credits and carry a real
  // relationship. Sort is stable, so ranking on the relationship alone preserves the
  // AI band/order within each group and leaves a pure-Apollo list untouched.
  const allCandidates = [...candidates, ...hubspotOnly].sort(
    (a, b) =>
      RELATIONSHIP_RANK[relationshipOf(a.apolloId).kind] -
      RELATIONSHIP_RANK[relationshipOf(b.apolloId).kind]
  );
  const promotionPlan = hubspotResult?.promotion ?? {};
  // One rule for "what would promoting this person do", shared with the payload below and
  // the checkbox column, so the row you can tick and the row that gets promoted agree.
  const eligibilityOf = (id: string) =>
    promotionEligibility(promotionPlan, id, primaryKnownIds.has(id));

  // Live remaining enrichment credits (for the pre-pull hint) AND the environment's
  // `configured` flag. Fetched while expanded, and ALSO whenever this cell would
  // otherwise sit in a "waiting for contacts" state: on an environment with no Apollo
  // key the auto-kickoff no-ops silently (no operation, no marker, no blob ever), so
  // without this signal the cell waits forever for a run that will never start - which
  // is exactly how prod ran for months before anyone noticed. One shared query key, so
  // the cells on screen collapse into a single request.
  const apolloQuota = useApolloQuota(
    expanded || (hasCompany && primaryListBlob === null)
  );
  const creditsLeft = apolloQuota.data?.enrichmentCredits?.left ?? null;
  // Explicitly false only once the endpoint has answered; undefined while loading or
  // if the read failed, so a transient error never mislabels a working env as broken.
  const apolloUnconfigured = apolloQuota.data?.configured === false;

  const selectedArr = [...selectedIds];
  // Ticked people we already hold an address for: promoting them spends nothing. Read
  // from the BE's promotion plan rather than re-derived here, so the price quoted is the
  // price the promote handler actually charges.
  const freeFromHubSpot = selectedArr.filter(
    (id) => !enrichedIds.has(id) && eligibilityOf(id) === "free"
  );
  // Ticked people a paid Apollo reveal would actually run for. Excludes the blocked: the
  // BE drops them from paid_ids, so counting them here quoted a credit we never spend.
  const chargeable = selectedArr.filter(
    (id) => !enrichedIds.has(id) && eligibilityOf(id) === "chargeable"
  );
  const promotable = freeFromHubSpot.length + chargeable.length;

  const toggleSelect = (apolloId: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(apolloId)) next.delete(apolloId);
      else next.add(apolloId);
      return next;
    });

  // The HubSpot refresh polls the same way the other two operations do; on completion it
  // invalidates the lead so the adorners repaint from what the worker actually wrote.
  const hubSpotPoll = useOperation(
    hubSpotOp?.key ?? null,
    hubSpotOp?.siteId ?? null
  );
  const hubspotRefreshing =
    hubSpotOp !== null &&
    !["completed", "failed", "cancelled"].includes(
      hubSpotPoll.data?.operation?.status ?? ""
    );
  useEffect(() => {
    const operation = hubSpotPoll.data?.operation;
    if (!hubSpotOp || !operation) return;
    if (["completed", "failed", "cancelled"].includes(operation.status)) {
      setHubSpotOp(null);
      if (operation.status !== "completed") {
        setHubSpotError("The HubSpot refresh did not finish. Try again.");
      }
      void queryClient.invalidateQueries(["lead", leadId]);
      void queryClient.invalidateQueries(["leads"]);
    }
  }, [hubSpotPoll.data, hubSpotOp, queryClient, leadId]);

  const opPoll = useOperation(op?.key ?? null, op?.siteId ?? null);
  const status = opPoll.data?.operation?.status;
  const running =
    op !== null && !["completed", "failed", "cancelled"].includes(status ?? "");
  // A bulk run fans ONE operation across many (lead, role) pairs, and every cell in
  // the panel polls it. Rendering the operation's own "4 of 6" in each cell told the
  // operator nothing about the cell they were looking at - the running one, a queued
  // one and a finished one all read identically. The worker names the pair in flight,
  // so each cell reports its own state instead of the batch's.
  const progressDetail = opPoll.data?.operation?.progress?.detail;
  const runningPairIsMine =
    progressDetail === undefined ||
    (progressDetail.subjectRef === leadId && progressDetail.role === role);
  const progressMessage = running
    ? runningPairIsMine
      ? "Finding people..."
      : "Queued..."
    : undefined;

  useEffect(() => {
    if (!op) return;
    const operation = opPoll.data?.operation;
    if (!operation) return;
    if (operation.status === "completed") {
      const result = operation.result ?? {};
      const resultStatus =
        typeof result.status === "string" ? result.status : "";
      if (resultStatus === "not_configured") {
        setError("Apollo People Search isn't configured on this environment.");
      } else if (resultStatus === "no_company") {
        setError(
          `No ${role} company chosen for this lead - pick one before finding people.`
        );
      } else if (resultStatus === "quota_exhausted") {
        setError("Apollo's daily discovery quota is used up - try tomorrow.");
      } else if (resultStatus === "no_people") {
        setError(
          `Apollo found ${companyName} but no matching people in the UK.`
        );
      }
      // Refetch the lead so contactCandidates (and this cell) update. The BE no
      // longer bumps the lead's updatedAt on a people write, so this updates the
      // result in place without remounting (and collapsing) the cell.
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["lead", leadId]);
      // Re-ask what's still live: the cached response still names this now-finished op,
      // and the "checking for contacts" hint keys on that key being non-null.
      queryClient.invalidateQueries(["leadActivePeopleOps", leadId, role]);
      opPoll.remove();
      setOp(null);
    } else if (
      operation.status === "failed" ||
      operation.status === "cancelled"
    ) {
      setError("Finding people failed - see the operations page.");
      setOp(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opPoll.data?.operation?.status]);

  const pullOpPoll = useOperation(pullOp?.key ?? null, pullOp?.siteId ?? null);
  const pullStatus = pullOpPoll.data?.operation?.status;
  const pullRunning =
    pullOp !== null &&
    !["completed", "failed", "cancelled"].includes(pullStatus ?? "");
  const pullProgress = pullOpPoll.data?.operation?.progress?.message;

  useEffect(() => {
    if (!pullOp) return;
    const operation = pullOpPoll.data?.operation;
    if (!operation) return;
    if (operation.status === "completed") {
      const result = operation.result ?? {};
      const rs = typeof result.status === "string" ? result.status : "";
      if (rs === "not_configured") {
        setPullError("Apollo enrichment isn't configured on this environment.");
      } else if (rs === "quota_exhausted") {
        // Covers BOTH a pre-gate empty balance AND a mid-run 429; the BE puts the
        // specific reason in result.error, so prefer it (mirrors stale_company below).
        // A genuinely empty balance must not read as a transient "try again shortly".
        setPullError(
          typeof result.error === "string"
            ? result.error
            : "Apollo rate/credit limit hit mid-pull - try again shortly."
        );
      } else if (rs === "no_candidates") {
        setPullError("No discovered people to pull for this role yet.");
      } else if (rs === "stale_company") {
        // The operator changed the company mid-pull, so the reveals were discarded
        // (they belonged to the old company). Not a success - say so plainly.
        setPullError(
          typeof result.error === "string"
            ? result.error
            : "The company changed while pulling - those contacts were discarded."
        );
      } else if (rs === "error") {
        setPullError("Pulling contact info failed - try again.");
      } else {
        const enriched = Number(result.enrichedCount ?? 0);
        const skipped = Number(result.skippedCount ?? 0);
        const missing = Number(result.missingCount ?? 0);
        const credits = Number(result.creditsConsumed ?? 0);
        const parts = [
          `Pulled ${enriched} contact${enriched === 1 ? "" : "s"}`,
        ];
        if (missing) parts.push(`${missing} with no match`);
        if (skipped) parts.push(`${skipped} already had details`);
        parts.push(`${credits} credit${credits === 1 ? "" : "s"} used`);
        setPullMessage(parts.join(" · "));
        // Clear the ticks ONLY on a successful pull. On a failure/partial (quota hit
        // mid-run, stale company, error) keep them so the operator can retry exactly
        // the ids left un-attempted - a re-pull skips the ids already pulled.
        setSelectedIds(new Set());
      }
      // Refresh the lead (details tile + done markers) + the credit balance.
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["lead", leadId]);
      queryClient.invalidateQueries(["apolloQuota"]);
      queryClient.invalidateQueries(["leadActivePeopleOps", leadId, role]);
      pullOpPoll.remove();
      setPullOp(null);
    } else if (
      operation.status === "failed" ||
      operation.status === "cancelled"
    ) {
      setPullError("Pulling contact info failed - see the operations page.");
      setPullOp(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullOpPoll.data?.operation?.status]);

  const handleFind = async () => {
    setError(null);
    try {
      const res = await findLeadPeople.mutateAsync({
        leadId,
        role,
        domainOverride: domainInput.trim() || undefined,
      });
      setOp({ key: res.operationKey, siteId: res.siteId });
    } catch (e) {
      const detail =
        (e as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? null;
      setError(
        detail
          ? `Could not start the people search: ${detail}`
          : "Could not start the people search. Try again."
      );
    }
  };

  // Refresh runs as an LRO, so it is polled like the other two rather than resolving on
  // the mutation - the panel repaints when the worker has actually written.
  const handleHubSpotRefresh = async () => {
    setHubSpotError(null);
    try {
      const res = await refreshHubSpot.mutateAsync({ leadId, role });
      setHubSpotOp({ key: res.operationKey, siteId: res.siteId });
    } catch {
      setHubSpotError("Could not start the HubSpot refresh. Try again.");
    }
  };

  const handlePull = async () => {
    setPullError(null);
    setPullMessage(null);
    try {
      const res = await pullContactInfo.mutateAsync({
        leadId,
        role,
        apolloIds: [...freeFromHubSpot, ...chargeable],
      });
      if (res.status === "complete") {
        // Everyone selected was already ours, so there was nothing to enqueue and no
        // operation to poll - the promoted contacts are already written. Refetch and say
        // what happened rather than spinning on a poll that will never resolve.
        setSelectedIds(new Set());
        void queryClient.invalidateQueries(["lead", leadId]);
        void queryClient.invalidateQueries(["leads"]);
        setPullMessage(
          `${res.promotedFromHubSpot ?? 0} promoted from HubSpot, no Apollo credits used` +
            (res.blocked ? ` · ${res.blocked} skipped (opted out)` : "")
        );
        return;
      }
      if (res.deduped) {
        // An identical pull for this lead + role was already in flight, so the BE
        // coalesced onto it instead of starting a second one - that running pull may
        // not include this selection, so these ids could go unpulled. Tell the
        // operator to re-run once it finishes (the ticks are kept, so it is one
        // click). We don't poll the other pull's op here - its result is not this
        // selection's.
        setPullError(
          `A contact pull for this ${role} is already running for this lead - your selection may not be included. Wait for it to finish, then click "Promote to contact list" again.`
        );
        return;
      }
      if (res.operationKey) {
        setPullOp({ key: res.operationKey, siteId: res.siteId });
      }
    } catch {
      setPullError("Could not start the contact pull. Try again.");
    }
  };

  const kicking = findLeadPeople.isLoading && op === null;
  const pullKicking = pullContactInfo.isLoading && pullOp === null;
  const pullBusy = pullRunning || pullKicking;

  // The select cell shown at the head of each people row: a "done" tick for an
  // already-pulled person (not re-selectable - re-pull is skip-only), otherwise a
  // checkbox. A legacy row without an apolloId (discovered before this shipped) is
  // simply not selectable.
  const selectCell = (apolloId: string | undefined) => {
    if (!apolloId) return <td />;
    if (enrichedIds.has(apolloId)) {
      return (
        <td
          className="text-center"
          title="Contact info already pulled"
          style={{ color: "#0f5132", fontWeight: 700 }}
        >
          ✓
        </td>
      );
    }
    const eligibility = eligibilityOf(apolloId);
    if (eligibility !== "free" && eligibility !== "chargeable") {
      // Two genuinely un-promotable cases, and they need different explanations: someone
      // who opted out (we hold an address we must not use), and someone pooled in from
      // the other role's ranking, whom the role-keyed pull would drop server-side. Shown
      // either way - not hidden - just not selectable.
      return (
        <td
          className="text-center text-muted"
          title={
            eligibility === "blocked"
              ? "Opted out of contact - cannot be promoted"
              : "Found under the landlord ranking - not pullable from the pooled view"
          }
          style={{ fontSize: 11 }}
        >
          –
        </td>
      );
    }
    return (
      <td className="text-center">
        <Form.Check
          type="checkbox"
          checked={selectedIds.has(apolloId)}
          onChange={() => toggleSelect(apolloId)}
          disabled={pullBusy}
          style={{ margin: 0, minHeight: 0 }}
          aria-label={`Select ${apolloId} to pull contact info`}
        />
      </td>
    );
  };

  const hasSelectablePeople = allCandidates.length > 0 || eliminated.length > 0;

  const summary = hasCompany ? (
    <div style={{ fontSize: 12.5 }}>
      <span style={{ fontWeight: 600 }}>{companyName}</span>
      {roleResult &&
        (throttled ? (
          <span style={{ color: "#b26a00", fontWeight: 600 }}>
            {" · "}
            Throttled - retry
          </span>
        ) : notRun ? (
          <span style={{ color: "#6c757d", fontWeight: 600 }}>
            {" · "}
            Not run yet - click Find people
          </span>
        ) : (
          <span style={{ color: "#6c757d" }}>
            {" · "}
            {candidates.length}/{consideredCount} contacts
          </span>
        ))}
      {!expanded && candidates.length > 0 && (
        <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}>
          {candidates.slice(0, PREVIEW_LIMIT).map((p) => (
            <li
              key={p.apolloId}
              style={{
                display: "flex",
                gap: 8,
                padding: "3px 0",
                borderTop: "1px solid #f0f1f3",
                fontSize: 12,
              }}
            >
              <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {candidateDisplayName(p, relationshipOf(p.apolloId))}
              </span>
              <span
                style={{
                  color: "#6c757d",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {p.title}
              </span>
            </li>
          ))}
          {candidates.length > PREVIEW_LIMIT && (
            <li style={{ color: "#9aa2ad", fontSize: 11.5, padding: "3px 0" }}>
              +{candidates.length - PREVIEW_LIMIT} more
            </li>
          )}
        </ul>
      )}
    </div>
  ) : (
    <span style={{ color: "#6c757d", fontSize: 12.5 }}>
      (choose a {role} first)
    </span>
  );

  // People were found but the AI ranking kept none of them - make that legible in the
  // collapsed cell (the excluded list is only in the expanded body), so it doesn't
  // read as "nothing found".
  const noneRelevant =
    roleResult != null && candidates.length === 0 && eliminated.length > 0;

  // Rendered at the TOP of the expanded body (where it explains the zero count sitting
  // right under it) and in the footer only while collapsed (where the expanded body,
  // and so the excluded list, is not on screen at all).
  const noneRelevantNotice = noneRelevant ? (
    <div style={{ fontSize: 12, color: "#664d03", marginBottom: 6 }}>
      Contacts found, none relevant
      {expanded ? " - see the excluded list below." : ", expand to view."}
    </div>
  ) : null;

  const footer = (
    <>
      {!expanded && noneRelevantNotice}
      <div
        className="d-flex align-items-center gap-2 flex-wrap"
        style={{ marginTop: 8 }}
      >
        {hasCompany && (
          <Form.Control
            type="text"
            size="sm"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="optional domain (e.g. company.co.uk)"
            disabled={running || kicking}
            style={{ maxWidth: 240, fontSize: 12 }}
          />
        )}
        <Button
          size="sm"
          variant="outline-primary"
          disabled={
            !hasCompany ||
            apolloUnconfigured ||
            running ||
            kicking ||
            discoveryPending
          }
          onClick={() => void handleFind()}
        >
          {running || kicking || discoveryPending ? (
            <>
              <Spinner size="sm" animation="border" className="me-1" />
              {progressMessage ??
                (running || kicking ? "Finding people..." : "Checking...")}
            </>
          ) : roleResult && !notRun ? (
            "Re-find people (free)"
          ) : (
            "Find people (free)"
          )}
        </Button>
        {discoveryPending && !running && !kicking && (
          // The button itself carries the spinner + disabled state, so this is a plain
          // explanation of what it is waiting for rather than a second spinner beside an
          // enabled button (which read as "idle" and invited a click that raced the
          // auto-kicked run).
          <span className="text-muted" style={{ fontSize: 12 }}>
            Checking for an automatic contacts run...
          </span>
        )}
        {apolloUnconfigured && !running && !kicking && (
          // The environment has no Apollo key, so nothing will ever run here. Say so
          // rather than resting on an empty cell the operator reads as "still loading".
          <span style={{ fontSize: 12, color: "#664d03" }}>
            Apollo isn&apos;t configured on this environment, so no contacts can
            be found.
          </span>
        )}
      </div>
      {error && (
        <Alert variant="warning" className="mt-2 mb-1 py-1 px-2">
          {error}
        </Alert>
      )}
    </>
  );

  return (
    <>
      <CollapsibleCell
        title={role}
        summary={summary}
        footer={footer}
        expanded={expanded}
        onToggle={onToggle}
        span={span}
      >
        {roleResult ? (
          <>
            {noneRelevantNotice}
            <div
              className="text-muted d-flex align-items-center gap-2 flex-wrap"
              style={{ fontSize: 12 }}
            >
              <span>
                {throttled ? (
                  <span style={{ color: "#b26a00", fontWeight: 600 }}>
                    Throttled at{" "}
                    {new Date(roleResult.discoveredAt).toLocaleString()}
                  </span>
                ) : notRun ? (
                  <span style={{ color: "#6c757d", fontWeight: 600 }}>
                    Not run yet - the bulk contacts run didn&apos;t reach this
                    one
                  </span>
                ) : (
                  <>
                    {candidates.length} people from {consideredCount}
                    {notConsideredCount > 0
                      ? ` considered of ${roleResult.apolloTotalEntries}`
                      : ""}{" "}
                    at {roleResult.companyName}
                    {roleResult.sampled && " (sampled)"} -{" "}
                    {new Date(roleResult.discoveredAt).toLocaleString()}
                  </>
                )}
              </span>
              {/* Only offer the button when a ranking trace exists (its run_id is on the
                  role blob). A throttle / not_run marker / no_people run stores none. */}
              {!throttled && !notRun && roleResult.traceRunId && (
                <Button
                  variant="link"
                  className="p-0"
                  style={{ fontSize: 12, verticalAlign: "baseline" }}
                  onClick={() =>
                    setTraceModal({
                      runId: roleResult.traceRunId!,
                      title: `Contact ranking - AI trace (${role})`,
                    })
                  }
                >
                  view contact ranking trace
                </Button>
              )}
            </div>

            {roleResult.domain && (
              <div className="mt-1" style={{ fontSize: 12 }}>
                <span style={{ color: "#0a7d55", fontWeight: 600 }}>
                  Domain used: {roleResult.domain}
                </span>
                {roleResult.domainSource === "ai" && (
                  <span className="text-muted">
                    {" (found by AI"}
                    {roleResult.domainTraceRunId && (
                      <>
                        {" - "}
                        <Button
                          variant="link"
                          className="p-0"
                          style={{ fontSize: 12, verticalAlign: "baseline" }}
                          onClick={() =>
                            setTraceModal({
                              runId: roleResult.domainTraceRunId!,
                              title: `Domain resolution - AI trace (${role})`,
                            })
                          }
                        >
                          view trace
                        </Button>
                      </>
                    )}
                    {")"}
                  </span>
                )}
                {roleResult.domainSource === "captured" && (
                  <span className="text-muted">
                    {" "}
                    (from the website on file)
                  </span>
                )}
                {roleResult.domainSource === "override" && (
                  <span className="text-muted"> (you entered this)</span>
                )}
              </div>
            )}

            {/* The AI domain resolver ran but adopted no domain (it wasn't confident, so
                Apollo fell back to a name search). No domain to show, but the pass left a
                trace - surface it so the operator can see WHY it gave up, rather than the
                button silently vanishing as it did before. */}
            {!roleResult.domain && roleResult.domainTraceRunId && (
              <div className="mt-1" style={{ fontSize: 12 }}>
                <span className="text-muted">
                  AI domain search found no confident domain - searched by name.{" "}
                  <Button
                    variant="link"
                    className="p-0"
                    style={{ fontSize: 12, verticalAlign: "baseline" }}
                    onClick={() =>
                      setTraceModal({
                        runId: roleResult.domainTraceRunId!,
                        title: `Domain resolution - AI trace (${role}, no domain found)`,
                      })
                    }
                  >
                    view trace
                  </Button>
                </span>
              </div>
            )}

            <HubSpotSummary
              hubspot={hubspotResult}
              busy={refreshHubSpot.isLoading || hubspotRefreshing}
              onRefresh={() => void handleHubSpotRefresh()}
              onViewTrace={() =>
                hubspotResult?.traceRunId
                  ? setTraceModal({
                      runId: hubspotResult.traceRunId,
                      title: "HubSpot matching",
                    })
                  : undefined
              }
            />

            {searchAttempts.length > 0 && !searchLogRedundant && (
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                {searchAttempts.length > 1 ? "Searches run: " : "Searched: "}
                {searchAttempts
                  .map((a) =>
                    a.kind === "domain"
                      ? `domain ${a.query}: ${a.totalEntries}`
                      : `name "${a.query}": ${a.totalEntries}`
                  )
                  .join(" · ")}
              </div>
            )}

            {allCandidates.length === 0 ? (
              <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                {throttled
                  ? "Apollo's daily discovery quota was used up when this ran - retry to search again."
                  : notRun
                    ? 'The bulk contacts run didn’t reach this one - click "Find people (free)" below to run it now.'
                    : "No people to show."}
              </div>
            ) : (
              <ScrollableRows rowCount={allCandidates.length}>
                <Table
                  size="sm"
                  className="mt-2 mb-1 align-middle"
                  style={{ fontSize: 12 }}
                >
                  <thead
                    style={{ position: "sticky", top: 0, background: "#fff" }}
                  >
                    <tr className="text-muted">
                      <th style={{ width: 26 }} />
                      <th>Name</th>
                      <th>Position</th>
                      <th style={{ width: 196 }}>Relationship</th>
                      <th>Priority</th>
                      <th>Reasoning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCandidates.map((person) => {
                      const relationship = relationshipOf(person.apolloId);
                      const blocked = relationship.kind === "do_not_contact";
                      return (
                        <tr key={person.apolloId}>
                          {blocked ? (
                            <td>
                              <Form.Check
                                type="checkbox"
                                disabled
                                checked={false}
                                readOnly
                                title="Opted out in HubSpot - cannot be contacted"
                              />
                            </td>
                          ) : (
                            selectCell(person.apolloId)
                          )}
                          <td
                            className="fw-semibold text-nowrap"
                            style={blocked ? { color: "#6c757d" } : undefined}
                          >
                            {candidateDisplayName(person, relationship)}
                          </td>
                          <td
                            style={blocked ? { color: "#6c757d" } : undefined}
                          >
                            {person.title}
                          </td>
                          <td>
                            <RelationshipCell
                              relationship={relationship}
                              busy={decideHubSpotMatch.isLoading}
                              onConfirm={() =>
                                decideHubSpotMatch.mutate({
                                  leadId,
                                  role,
                                  apolloId: person.apolloId,
                                  contactId:
                                    relationship.match?.contactId ?? "",
                                  verdict: "confirmed",
                                })
                              }
                              onReject={() =>
                                decideHubSpotMatch.mutate({
                                  leadId,
                                  role,
                                  apolloId: person.apolloId,
                                  contactId:
                                    relationship.match?.contactId ?? "",
                                  verdict: "rejected",
                                })
                              }
                            />
                          </td>
                          <td className="text-nowrap">
                            {unranked.has(person.apolloId)
                              ? "-"
                              : bandLabel(person.band)}
                          </td>
                          <td className="text-muted">
                            {unranked.has(person.apolloId)
                              ? "Not yet ranked - added to HubSpot after this run"
                              : person.reasoning}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </ScrollableRows>
            )}

            {eliminated.length > 0 && (
              <div className="mt-1">
                <Button
                  variant="link"
                  className="p-0"
                  style={{ fontSize: 12, verticalAlign: "baseline" }}
                  onClick={() => setShowEliminated((v) => !v)}
                >
                  {showEliminated ? "Hide" : "Show"} {eliminated.length}{" "}
                  excluded by the AI ranking
                </Button>
                {showEliminated && (
                  <ScrollableRows rowCount={eliminated.length}>
                    <Table
                      size="sm"
                      className="mt-1 mb-1 align-middle"
                      style={{ fontSize: 12 }}
                    >
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                        }}
                      >
                        <tr className="text-muted">
                          <th style={{ width: 26 }} />
                          <th>Name</th>
                          <th>Position</th>
                          <th>Why excluded</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eliminated.map((p, i) => (
                          <tr
                            key={p.apolloId ?? `${p.firstName}-${p.title}-${i}`}
                          >
                            {selectCell(p.apolloId)}
                            <td className="text-nowrap">
                              {candidateDisplayName(
                                p,
                                p.apolloId ? relationshipOf(p.apolloId) : null
                              )}
                            </td>
                            <td>{p.title}</td>
                            <td className="text-muted">{p.reasoning || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ScrollableRows>
                )}
              </div>
            )}

            {/* Pull action - BELOW both tables so it clearly applies to everyone
                ticked (ranked + excluded). Reveals email/title/LinkedIn for the new
                selections; already-pulled rows are skipped (no re-charge). */}
            {hasSelectablePeople && (
              <div
                className="d-flex align-items-center gap-2 flex-wrap"
                style={{ marginTop: 10 }}
              >
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={promotable === 0 || pullBusy}
                  onClick={() => void handlePull()}
                >
                  {pullBusy ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-1" />
                      {pullProgress ?? "Promoting..."}
                    </>
                  ) : (
                    "Promote to contact list"
                  )}
                </Button>
                {!pullBusy && promotable > 0 && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {selectedArr.length} selected
                    {freeFromHubSpot.length > 0 && (
                      <>
                        {" · "}
                        <span style={{ color: "#0f5132", fontWeight: 600 }}>
                          {freeFromHubSpot.length} free from HubSpot
                        </span>
                      </>
                    )}
                    {chargeable.length > 0 ? (
                      <>
                        {" · "}
                        <span style={{ color: "#212529", fontWeight: 600 }}>
                          {chargeable.length} paid
                        </span>
                        {", using "}
                        {chargeable.length}
                        {creditsLeft != null
                          ? ` of remaining ${creditsLeft}`
                          : ""}{" "}
                        Apollo credit{chargeable.length === 1 ? "" : "s"}
                      </>
                    ) : (
                      " · no Apollo credits needed"
                    )}
                  </span>
                )}
                {!pullBusy &&
                  chargeable.length === 0 &&
                  selectedArr.length > 0 && (
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      all selected already pulled
                    </span>
                  )}
                {/* Success confirmation: small + inline (right of the button), and
                    dismissable. It's cleared at the start of the next pull, so a fresh
                    run always overwrites the previous one. */}
                {!pullBusy && pullMessage && (
                  <span
                    className="d-inline-flex align-items-center"
                    style={{ fontSize: 12, color: "#0f5132" }}
                  >
                    {pullMessage}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-1"
                      style={{
                        fontSize: 14,
                        lineHeight: 1,
                        color: "#9aa2ad",
                        textDecoration: "none",
                      }}
                      onClick={() => setPullMessage(null)}
                      title="Dismiss"
                      aria-label="Dismiss confirmation"
                    >
                      ×
                    </Button>
                  </span>
                )}
              </div>
            )}
            {pullError && (
              <Alert variant="warning" className="mt-2 mb-1 py-1 px-2">
                {pullError}
              </Alert>
            )}
            {hubSpotError && (
              <Alert variant="warning" className="mt-2 mb-1 py-1 px-2">
                {hubSpotError}
              </Alert>
            )}
          </>
        ) : (
          <div className="text-muted" style={{ fontSize: 13 }}>
            No contacts yet - use &quot;Find people (free)&quot; below.
          </div>
        )}
      </CollapsibleCell>

      <PeopleTraceModal
        leadId={leadId}
        role={role}
        openTrace={traceModal}
        onHide={() => setTraceModal(null)}
      />
    </>
  );
};

// The expanded row in the contact list: everything HubSpot holds about this person,
// which is what a warm draft is written from. Rendered in place rather than as a popup
// so it reads as part of the list it belongs to.
const ContactRelationshipDetail: React.FC<{
  relationship: Relationship;
  onViewThread: () => void;
}> = ({ relationship, onViewThread }) => {
  const contact = relationship.contact;
  if (!contact) return null;
  const row = (label: string, value: React.ReactNode) => (
    <>
      <div style={{ color: "#6c757d" }}>{label}</div>
      <div>{value}</div>
    </>
  );
  return (
    <div style={{ padding: "10px 4px 12px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "150px minmax(0, 1fr)",
          gap: "7px 14px",
          fontSize: 12.5,
        }}
      >
        {contact.email && row("Email held", contact.email)}
        {contact.ownerId && row("Owner", contact.ownerId)}
        {(contact.lifecycleStage || contact.leadStatus) &&
          row(
            "Where they stand",
            [contact.lifecycleStage, contact.leadStatus]
              .filter(Boolean)
              .join(" · ")
          )}
        {contact.lastContacted &&
          row(
            "Last contacted",
            <span>
              {contact.lastContacted}{" "}
              <Button
                variant="link"
                className="p-0"
                style={{ fontSize: 12, verticalAlign: "baseline" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewThread();
                }}
              >
                View thread
              </Button>
            </span>
          )}
        {contact.noteCount != null &&
          row("Logged activity", `${contact.noteCount} notes`)}
        {relationship.kind === "moved" &&
          contact.companyName &&
          row(
            "We know them from",
            `${contact.companyName} - the address held there is not reused`
          )}
        {relationship.match?.reasoning &&
          row("Matched by", relationship.match.reasoning)}
        {relationship.decision &&
          row(
            "Confirmed by",
            `${relationship.decision.decidedBy} (${relationship.decision.verdict})`
          )}
      </div>
    </div>
  );
};

// The whole email history HubSpot holds for one contact, newest first. Read live when
// opened. An empty list on a linked contact means nothing is LOGGED in HubSpot, which is
// not the same as never contacted - so it says which rather than implying a clean slate.
const ContactThreadModal: React.FC<{
  show: boolean;
  onHide: () => void;
  leadId: string;
  role: PeopleRole;
  contact: EnrichedContact | null;
}> = ({ show, onHide, leadId, role, contact }) => {
  const thread = useLeadContactThread(
    leadId,
    role,
    contact?.apolloId ?? null,
    show
  );
  const emails = thread.data?.emails ?? [];
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 17 }}>
          Email thread
          <span
            className="text-muted"
            style={{ fontSize: 13, marginLeft: 8, fontWeight: 400 }}
          >
            {contact?.name} · from HubSpot
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {thread.isLoading ? (
          <div className="text-muted" style={{ fontSize: 13 }}>
            <Spinner size="sm" animation="border" className="me-2" />
            Reading HubSpot...
          </div>
        ) : thread.data?.error ? (
          <Alert variant="warning" className="mb-0" style={{ fontSize: 13 }}>
            Could not read the thread from HubSpot: {thread.data.error}
          </Alert>
        ) : emails.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 13 }}>
            {thread.data?.linked === false
              ? "This contact is not linked to a HubSpot record."
              : "Nothing is logged against this contact in HubSpot. That is not the same as never contacted - an email sent from Gmail without HubSpot logging leaves no trace here."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {emails.map((email, index) => (
              <div
                key={email.emailId}
                style={{
                  border: "1px solid #dee2e6",
                  borderLeft:
                    index === 0 ? "3px solid #0d6efd" : "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 12.5 }}>
                    {email.inbound ? "They wrote" : "We wrote"}
                  </span>
                  <span style={{ flexGrow: 1 }} />
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {email.sentAt ?? "no date"}
                  </span>
                </div>
                {email.subject && (
                  <div
                    style={{ fontWeight: 600, fontSize: 12.5, marginTop: 6 }}
                  >
                    {email.subject}
                  </div>
                )}
                {email.body && (
                  <div
                    style={{
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      marginTop: 5,
                      color: "#495057",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {email.body}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

// The pulled-contacts tile: a SEPARATE cell that sits beneath its role's people
// cell (tenant under tenant, landlord under landlord). Lists the revealed contacts -
// Name (links to LinkedIn) / Title / Email / Confidence chip - each with a disabled
// "Draft email" button (wired up in a future pass). Empty until the first pull.
// Offered only when Apollo filled a field HubSpot had blank, so the button never
// proposes writing HubSpot's own data back to it. The write itself re-checks each field
// is still empty, so a colleague filling it in first always wins.
const PushToHubSpotButton: React.FC<{
  leadId: string;
  role: PeopleRole;
  contact: EnrichedContact;
}> = ({ leadId, role, contact }) => {
  const push = useHubSpotPushContact(leadId);
  const filled = Object.keys(contact.apolloFilled ?? {});
  if (!contact.hubspotContactId || filled.length === 0) return null;
  if (contact.hubspotPushedAt) {
    return (
      <span
        className="text-muted"
        style={{ marginLeft: 5, fontSize: 11 }}
        title={`Pushed ${new Date(contact.hubspotPushedAt).toLocaleString()}`}
      >
        In HubSpot ✓
      </span>
    );
  }
  return (
    <>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={push.isLoading}
        onClick={() => push.mutate({ role, apolloId: contact.apolloId })}
        title={`Write ${filled.join(", ")} into this HubSpot contact - only fills fields HubSpot has blank`}
        style={{ whiteSpace: "nowrap", fontSize: 11, marginLeft: 5 }}
      >
        {push.isLoading ? "Pushing..." : "Push to HubSpot"}
      </Button>
      {push.isError && (
        <div style={{ color: "#842029", fontSize: 11, marginTop: 3 }}>
          {(push.error as { response?: { data?: { error?: string } } })
            ?.response?.data?.error ?? "Could not write to HubSpot."}
        </div>
      )}
    </>
  );
};

export const ContactDetailsCell: React.FC<{
  leadId: string;
  role: PeopleRole;
  contactEnrichments: ContactEnrichments | null;
  hubspotMatches: HubSpotMatches | null;
  expanded: boolean;
  onToggle: () => void;
  // Full-width row span - used when it's the sole details cell (owner-occupier).
  span?: boolean;
  // Pool this cell's pulled contacts with another role's (owner-occupier), deduped by
  // apolloId - mirrors ContactsRoleCell so the done-ticks above and the details below
  // always agree. null = no pooling. Required (see the "required typed props" rule).
  poolWithRole: PeopleRole | null;
}> = ({
  leadId,
  role,
  contactEnrichments,
  hubspotMatches,
  expanded,
  onToggle,
  span,
  poolWithRole,
}) => {
  const enrichResult = mergeContactEnrichments(
    contactEnrichments?.[role] ?? null,
    poolWithRole ? (contactEnrichments?.[poolWithRole] ?? null) : null
  );
  const contacts: EnrichedContact[] = enrichResult?.contacts ?? [];
  const companyName = enrichResult?.companyName ?? null;
  // The contact the operator is drafting an outreach email for (opens the studio).
  const [draftFor, setDraftFor] = useState<EnrichedContact | null>(null);
  // Which row is expanded, and whether its thread dialog is open. Expansion lives HERE
  // and not in the candidates cell: drafting happens in this list, so all the history a
  // draft is written from is read in one place rather than two.
  const [expandedContact, setExpandedContact] = useState<string | null>(null);
  const [threadFor, setThreadFor] = useState<EnrichedContact | null>(null);
  const hubspotResult = mergeHubSpotRoleResults(
    hubspotMatches?.[role] ?? null,
    poolWithRole ? (hubspotMatches?.[poolWithRole] ?? null) : null
  );
  const relationshipOf = (apolloId: string): Relationship =>
    relationshipFor(apolloId, hubspotResult, {
      hubspotOnly: apolloId.startsWith(HUBSPOT_ONLY_PREFIX),
    });
  // Existing draft threads for this lead, so a contact with a draft shows a "v{turns}"
  // badge on its Draft email button. Fetched only while expanded; refetched after a draft.
  // Key on THIS cell's role as well as the contact - the modal decides load-vs-draft on
  // (role, apolloId), so a same-apolloId thread under the OTHER role must not badge this
  // cell as "v{n}" (it would open and draft a fresh thread, contradicting the badge).
  const threadsQuery = useLeadOutreachThreads(leadId, expanded);
  const turnsByApollo = new Map<string, number>();
  for (const t of threadsQuery.data ?? []) {
    if (t.role === role && t.contactApolloId) {
      turnsByApollo.set(t.contactApolloId, t.turnCount);
    }
  }
  // Every ticked person gets a record (matched or missed) so skip-only can re-skip it,
  // so `attempted` = how many we tried, `withEmail` = how many came back with an email,
  // and `pulled` = the ones Apollo actually matched (what the preview lists).
  const attempted = contacts.length;
  const withEmail = contacts.filter((c) => !!c.email).length;
  const pulled = contacts.filter((c) => c.matched);

  // Mirror the people tile above: company + a "N/M" count, then a name/title preview
  // when collapsed. The full detail table lives in the expanded body below.
  const summary =
    attempted > 0 ? (
      <div style={{ fontSize: 12.5 }}>
        {companyName && <span style={{ fontWeight: 600 }}>{companyName}</span>}
        <span style={{ color: "#6c757d" }}>
          {companyName ? " · " : ""}
          {withEmail}/{attempted} with email
        </span>
        {!expanded && pulled.length > 0 && (
          <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}>
            {pulled.slice(0, PREVIEW_LIMIT).map((c) => (
              <li
                key={c.apolloId}
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "3px 0",
                  borderTop: "1px solid #f0f1f3",
                  fontSize: 12,
                }}
              >
                <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  {c.name}
                </span>
                <span
                  style={{
                    color: "#6c757d",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.title}
                </span>
              </li>
            ))}
            {pulled.length > PREVIEW_LIMIT && (
              <li
                style={{ color: "#9aa2ad", fontSize: 11.5, padding: "3px 0" }}
              >
                +{pulled.length - PREVIEW_LIMIT} more
              </li>
            )}
          </ul>
        )}
      </div>
    ) : (
      <span style={{ fontSize: 12.5, color: "#9aa2ad" }}>
        No contacts pulled yet
      </span>
    );

  return (
    <CollapsibleCell
      title={`${role} · contact details`}
      summary={summary}
      expanded={expanded}
      onToggle={onToggle}
      span={span}
    >
      {contacts.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 13 }}>
          Tick people in the {role} tile above, then use &quot;Pull contact
          info&quot;.
        </div>
      ) : (
        <Table
          size="sm"
          className="mt-1 mb-1 align-middle"
          style={{ fontSize: 12 }}
        >
          <thead>
            <tr className="text-muted">
              <th style={{ width: 18 }} />
              <th>Name</th>
              <th>Title</th>
              <th>Email</th>
              <th style={{ width: 176 }}>Relationship</th>
              <th>Confidence</th>
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => {
              const relationship = relationshipOf(c.apolloId);
              const known = relationship.kind !== "apollo_only";
              const isExpanded = expandedContact === c.apolloId;
              return (
                <React.Fragment key={c.apolloId}>
                  <tr
                    onClick={() =>
                      known
                        ? setExpandedContact(isExpanded ? null : c.apolloId)
                        : undefined
                    }
                    style={known ? { cursor: "pointer" } : undefined}
                  >
                    <td>
                      {known && (
                        <span style={{ color: "#6c757d", fontSize: 11 }}>
                          {isExpanded ? "\u25be" : "\u25b8"}
                        </span>
                      )}
                    </td>
                    <td className="fw-semibold text-nowrap">
                      {c.linkedinUrl ? (
                        <a
                          href={c.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open LinkedIn profile"
                        >
                          {c.name}
                          <span
                            style={{
                              fontSize: 8.5,
                              fontWeight: 700,
                              color: "#fff",
                              background: "#0a66c2",
                              borderRadius: 3,
                              padding: "0 3px",
                              marginLeft: 5,
                              verticalAlign: "middle",
                            }}
                          >
                            in
                          </span>
                        </a>
                      ) : (
                        c.name
                      )}
                    </td>
                    <td>{c.title || "-"}</td>
                    <td>
                      {c.email ? (
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>
                          {c.email}
                        </span>
                      ) : (
                        <span className="text-muted">- no email found</span>
                      )}
                    </td>
                    <td>
                      <RelationshipCell relationship={relationship} />
                    </td>
                    <td>
                      {c.emailSource === "hubspot" ? (
                        // An address HubSpot gave us never went through Apollo's
                        // verification, so it carries its own source rather than borrowing a
                        // confidence word that would misdescribe where it came from.
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: 999,
                            padding: "2px 9px",
                            whiteSpace: "nowrap",
                            background: "#d1e7dd",
                            color: "#0f5132",
                          }}
                        >
                          From HubSpot
                        </span>
                      ) : (
                        <ConfidenceChip contact={c} />
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant={
                          turnsByApollo.has(c.apolloId)
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() => setDraftFor(c)}
                        title={
                          turnsByApollo.has(c.apolloId)
                            ? "A draft already exists for this contact - open to view or rerun"
                            : "Draft a cold-outreach email to this contact"
                        }
                        style={{ whiteSpace: "nowrap", fontSize: 11 }}
                      >
                        Draft email
                        {turnsByApollo.has(c.apolloId) && (
                          <span
                            style={{
                              marginLeft: 5,
                              fontSize: 9.5,
                              fontWeight: 700,
                              background: "#fff",
                              color: "#0a58ca",
                              borderRadius: 3,
                              padding: "0 4px",
                              verticalAlign: "middle",
                            }}
                          >
                            v{turnsByApollo.get(c.apolloId)}
                          </span>
                        )}
                      </Button>
                      <PushToHubSpotButton
                        leadId={leadId}
                        role={role}
                        contact={c}
                      />
                      {known && relationship.kind !== "do_not_contact" && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            marginLeft: 5,
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: 999,
                            padding: "1px 8px",
                            background:
                              relationship.kind === "moved"
                                ? "#cfe2ff"
                                : "#d1e7dd",
                            color:
                              relationship.kind === "moved"
                                ? "#084298"
                                : "#0f5132",
                          }}
                          title="We have contacted this person before - the draft opens warm"
                        >
                          Warm
                        </span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && relationship.contact && (
                    <tr>
                      <td colSpan={7} style={{ background: "#f6fbf8" }}>
                        <ContactRelationshipDetail
                          relationship={relationship}
                          onViewThread={() => setThreadFor(c)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
      <ContactThreadModal
        show={!!threadFor}
        onHide={() => setThreadFor(null)}
        leadId={leadId}
        role={role}
        contact={threadFor}
      />
      <OutreachDraftModal
        show={!!draftFor}
        onHide={() => setDraftFor(null)}
        leadId={leadId}
        role={role}
        contact={draftFor}
        companyName={companyName}
        onViewThread={() => setThreadFor(draftFor)}
      />
    </CollapsibleCell>
  );
};

const PeopleTraceModal: React.FC<{
  leadId: string;
  role: PeopleRole;
  // The trace to show (run_id + modal title), or null when closed. One viewer serves
  // both the contact-ranking trace and the domain-resolution trace.
  openTrace: { runId: string; title: string } | null;
  onHide: () => void;
}> = ({ leadId, role, openTrace, onHide }) => {
  // The two lead-AI surfaces this cell can open store under different trace prefixes, so
  // the fetch is routed by which one was asked for rather than a second modal component.
  const isHubSpotTrace = openTrace?.title === "HubSpot matching";
  const peopleTrace = usePeopleDiscoveryTrace(
    `${leadId}:${role}`,
    openTrace?.runId ?? null,
    openTrace !== null && !isHubSpotTrace
  );
  const hubspotTrace = useHubSpotMatchTrace(
    `${leadId}:${role}`,
    openTrace?.runId ?? null,
    openTrace !== null && isHubSpotTrace
  );
  const traceQuery = isHubSpotTrace ? hubspotTrace : peopleTrace;
  return (
    <Modal show={openTrace !== null} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{openTrace?.title ?? "AI trace"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {traceQuery.isLoading ? (
          <Spinner animation="border" />
        ) : traceQuery.data ? (
          <AITraceViewer trace={traceQuery.data} />
        ) : (
          <div className="text-muted">No AI trace available.</div>
        )}
      </Modal.Body>
    </Modal>
  );
};
